// ============================================================================
// AUDIT LOGS VIEW
//
// Columns: Date · Referral ID · Specialty/Pack · Outcome · Submitted ·
//          Status (live workflow status) · View
//
// FIRESTORE READS:
//   PRIMARY:   tenants/{tenantId}/audit_logs (flat collection)
//   SECONDARY: tenants/{tenantId}/referrals/{referralId}/workflow/state
//              (batch-fetched after log load to show current funnel position)
//
// NAME RESOLUTION PRIORITY:
//   1) createdByName  — denormalized snapshot in audit_log doc
//   2) createdByEmail — denormalized email prefix
//   3) /users/{uid}   — runtime fetch via getTenantUser
//   4) truncated UID  — fallback
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { safeUserError } from '../core/safeError.js';
import {
  FileSearch, Clock, AlertTriangle, Loader2, RefreshCw,
  Calendar, CheckCircle, AlertCircle, Download,
  Filter, X, ChevronDown, User, ShieldOff, RotateCcw,
  Eye, ArrowRight, ClipboardList, FileText, Shield,
  Activity, CheckCircle2, GitBranch,
} from 'lucide-react';
import { getApp } from 'firebase/app';
import { getFirestore, doc, getDoc, collection, query, getDocs, orderBy } from 'firebase/firestore';
import {
  getAuditLogsForView, getSpecialties, getTenantUser,
  getReferral, getPacks, getPatientIdentity, getContact,
  getInsurance, listAttachments, getWorkflowState,
  getMetricsByReferralId,
} from '../core/index.js';
import PATHS from '../core/firestorePaths.js';
import { buildFullReceiptModel } from '../core/receipt/buildFullReceiptModel.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { getAuditViewLevel, AUDIT_VIEW_LEVELS } from '../core/audit.js';
import ReceiptModal from '../components/ReceiptModal.jsx';
import { RGStatusBadge } from '../components/rg/RGPrimitives.jsx';
import { getWorkflowStatusMeta } from '../core/statusConfig.js';

const PAGE_SIZE = 50;

// ── Display-name resolution ───────────────────────────────────────────────────
const getDisplayName = (log, usersMap) => {
  if (log.createdByName) return log.createdByName;
  if (log.createdByEmail) return log.createdByEmail.split('@')[0];
  const uid = log.createdByUid;
  if (!uid) return 'Unknown';
  const user = usersMap[uid];
  if (user && !user._failed) {
    if (user.displayName) return user.displayName;
    if (user.name) return user.name;
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    if (user.firstName) return user.firstName;
    if (user.email) return user.email.split('@')[0];
  }
  return uid.substring(0, 8) + '…';
};

// Status/outcome badge visuals are centralized in src/core/statusConfig.js and
// rendered via <RGStatusBadge>. No local status color maps live here.

// ── Format date/time ──────────────────────────────────────────────────────────
const formatDateTime = (timestamp) => {
  if (!timestamp) return 'N/A';
  try {
    const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return 'N/A';
  }
};

const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  try {
    const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return 'N/A';
  }
};

// ── Referral ID helper ────────────────────────────────────────────────────────
const getReferralId = (log) => log.referralId || log.runId || log.id || '—';

// ============================================================================
// WORKFLOW EVENT HELPERS
// ============================================================================

const EVENT_TYPE_CONFIG = {
  WORKFLOW_CREATED:           { label: 'Referral Created',       icon: FileText,      color: 'blue'   },
  STATUS_CHANGED:             { label: 'Status Changed',         icon: ArrowRight,    color: 'indigo' },
  ENTERED_OPS:                { label: 'Entered Operations',     icon: ClipboardList, color: 'teal'   },
  TRIAGE_UPDATED:             { label: 'Triage Updated',         icon: Shield,        color: 'violet' },
  TRIAGE_EXCEPTION_RECORDED:  { label: 'Triage Exception',       icon: AlertTriangle, color: 'orange' },
  REWORK_REQUEST_CREATED:     { label: 'Rework Requested',       icon: RefreshCw,     color: 'amber'  },
  REWORK_TOUCH_LOGGED:        { label: 'Rework Outreach',        icon: Activity,      color: 'slate'  },
  REWORK_RESOLVED:            { label: 'Rework Resolved',        icon: CheckCircle2,  color: 'emerald'},
  SCHEDULED_SET:              { label: 'Appointment Scheduled',  icon: Calendar,      color: 'teal'   },
  SEEN_SET:                   { label: 'Patient Seen',           icon: CheckCircle,   color: 'violet' },
  CLOSED_LOOP_SET:            { label: 'Closed Loop',            icon: CheckCircle2,  color: 'emerald'},
};

const TRIAGE_ACTION_LABELS = {
  TRIAGE_COMPLETED:       'Triage Completed',
  TRIAGE_ON_HOLD:         'Placed On Hold',
  TRIAGE_OVERRIDE_APPLIED:'Override Applied',
  TRIAGE_SAVED:           'Triage Saved',
};

const ROLE_LABELS = {
  gp_employee:        'Referring Provider',
  intake_coordinator: 'Intake Coordinator',
  triage_nurse:       'Triage Nurse',
  scheduler:          'Scheduler',
  specialist:         'Specialist',
  specialist_admin:   'Specialist Admin',
  tenant_admin:       'Tenant Admin',
  ops_manager:        'Ops Manager',
  system:             'System',
};

const fmtRole = (r) => ROLE_LABELS[r] ?? r ?? '—';

// ============================================================================
// AUDIT DETAIL MODAL
// ============================================================================
const AuditDetailModal = ({ log, workflowState, usersMap, tenantId, onClose }) => {
  const [events,       setEvents]       = useState([]);
  const [eventsLoading,setEventsLoading] = useState(true);
  const [eventsError,  setEventsError]  = useState(null);

  const referralId = getReferralId(log);
  const outcome    = log?.outcomeStatus;
  const wStatus    = workflowState?.status;

  // Fetch workflowEvents for this referral
  useEffect(() => {
    if (!tenantId || !referralId || referralId === '—') {
      setEventsLoading(false);
      return;
    }
    let cancelled = false;
    const db = getFirestore(getApp());
    const fetch = async () => {
      try {
        const colRef = collection(db, PATHS.col.workflowEvents(tenantId, referralId));
        const snap   = await getDocs(query(colRef, orderBy('createdAt', 'asc')));
        if (!cancelled) {
          setEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        }
      } catch (err) {
        if (!cancelled) setEventsError(safeUserError(err) ?? 'Failed to load activity');
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    };
    fetch();
    return () => { cancelled = true; };
  }, [tenantId, referralId]);

  if (!log) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-2xl bg-slate-900 border border-slate-700/60 rounded-2xl shadow-2xl flex flex-col max-h-[85vh]">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
              <FileSearch className="w-4 h-4 text-violet-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Audit Record</p>
              <p className="text-xs text-slate-500 font-mono">{referralId}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">

          {/* ── Gate Decision ── */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Gate Decision</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="bg-slate-800/50 rounded-xl p-3">
                <p className="text-xs text-slate-500 mb-1.5">Outcome</p>
                {outcome
                  ? <RGStatusBadge type="outcome" status={outcome} size="md" />
                  : <span className="text-xs text-slate-500">—</span>}
              </div>
              <div className="bg-slate-800/50 rounded-xl p-3">
                <p className="text-xs text-slate-500 mb-1.5">Current Status</p>
                {wStatus ? (
                  <RGStatusBadge type="workflow" status={wStatus} size="md" />
                ) : (
                  <span className="text-xs text-slate-500">—</span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-800/50 rounded-xl p-3">
                <p className="text-xs text-slate-500 mb-0.5">Specialty</p>
                <p className="text-sm text-slate-200">{log.specialtyId || '—'}</p>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-3">
                <p className="text-xs text-slate-500 mb-0.5">Pack</p>
                <p className="text-sm text-slate-200 truncate" title={log.packId}>{log.packId || '—'}</p>
                {log.packVersion && (
                  <p className="text-xs font-mono text-slate-600 mt-0.5">{log.packVersion}</p>
                )}
              </div>
            </div>
            {/* Flags */}
            {((log.attempt || 1) > 1 || log.overrideUsed) && (
              <div className="flex flex-wrap gap-2 mt-3">
                {(log.attempt || 1) > 1 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-400">
                    <RotateCcw className="w-3 h-3" /> Attempt {log.attempt}
                  </span>
                )}
                {log.overrideUsed && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-orange-500/20 text-orange-400">
                    <ShieldOff className="w-3 h-3" /> Override Applied
                  </span>
                )}
              </div>
            )}
          </div>

          {/* ── Referring Provider ── */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Referring Provider</p>
            <div className="bg-slate-800/30 border border-slate-700/40 rounded-xl divide-y divide-slate-700/40">
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-xs text-slate-500">Name</span>
                <div className="flex items-center gap-2">
                  <User className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-sm text-slate-200">{getDisplayName(log, usersMap)}</span>
                </div>
              </div>
              {log.createdByEmail && (
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-xs text-slate-500">Email</span>
                  <span className="text-sm text-slate-400">{log.createdByEmail}</span>
                </div>
              )}
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-xs text-slate-500">Submitted</span>
                <div className="flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-sm text-slate-200">{formatDateTime(log.createdAt)}</span>
                </div>
              </div>
              {workflowState?.assignedTo && (
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-xs text-slate-500">Assigned To (Ops)</span>
                  <span className="text-sm text-slate-400 font-mono text-xs">{workflowState.assignedTo.substring(0, 12)}…</span>
                </div>
              )}
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-xs text-slate-500">Patient Data</span>
                <span className="text-xs text-slate-600 italic">PHI — not accessible in audit context</span>
              </div>
            </div>
          </div>

          {/* ── Workflow Activity ── */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Workflow Activity</p>

            {eventsLoading && (
              <div className="flex items-center gap-2 text-slate-500 text-sm py-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading activity…
              </div>
            )}

            {eventsError && (
              <div className="flex items-center gap-2 text-red-400 text-sm py-2">
                <AlertCircle className="w-4 h-4" />
                {eventsError}
              </div>
            )}

            {!eventsLoading && !eventsError && events.length === 0 && (
              <p className="text-sm text-slate-600 py-2">No workflow events recorded yet.</p>
            )}

            {!eventsLoading && events.length > 0 && (
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-[15px] top-0 bottom-0 w-px bg-slate-700/60" />
                <div className="space-y-1">
                  {events.map((evt, idx) => {
                    const cfg  = EVENT_TYPE_CONFIG[evt.eventType] || { label: evt.eventType, icon: Activity, color: 'slate' };
                    const Icon = cfg.icon;
                    const isLast = idx === events.length - 1;

                    // Build descriptor line
                    let descriptor = null;
                    if (evt.eventType === 'STATUS_CHANGED' && evt.fromStatus && evt.toStatus) {
                      descriptor = (
                        <span className="flex items-center gap-1 flex-wrap">
                          <RGStatusBadge type="workflow" status={evt.fromStatus} />
                          <ArrowRight className="w-3 h-3 text-slate-600" />
                          <RGStatusBadge type="workflow" status={evt.toStatus} />
                        </span>
                      );
                    } else if (evt.eventType === 'TRIAGE_UPDATED' && evt.triageAction) {
                      descriptor = (
                        <span className="text-xs text-slate-400">{TRIAGE_ACTION_LABELS[evt.triageAction] ?? evt.triageAction}</span>
                      );
                    } else if (evt.eventType === 'TRIAGE_EXCEPTION_RECORDED' && evt.triageExceptionType) {
                      descriptor = (
                        <span className="text-xs text-orange-400/80">{evt.triageExceptionType.replace(/_/g, ' ')}</span>
                      );
                    } else if (evt.eventType === 'ENTERED_OPS' && evt.source) {
                      descriptor = (
                        <span className="text-xs text-slate-400">via {evt.source.toLowerCase()}</span>
                      );
                    }

                    return (
                      <div key={evt.id} className="relative flex gap-4 pb-1">
                        {/* Icon dot */}
                        <div className={`relative z-10 flex-shrink-0 w-8 h-8 rounded-full bg-slate-800 border border-slate-700/60 flex items-center justify-center`}>
                          <Icon className={`w-3.5 h-3.5 text-${cfg.color}-400`} />
                        </div>
                        {/* Content */}
                        <div className={`flex-1 min-w-0 bg-slate-800/40 rounded-xl px-3 py-2.5 ${isLast ? 'border border-slate-600/30' : ''}`}>
                          <div className="flex items-start justify-between gap-2 mb-0.5">
                            <span className="text-sm font-medium text-slate-200">{cfg.label}</span>
                            <span className="text-xs text-slate-600 flex-shrink-0">
                              {formatDateTime(evt.createdAt)}
                            </span>
                          </div>
                          {descriptor && <div className="mt-1">{descriptor}</div>}
                          {evt.actorRole && (
                            <p className="text-xs text-slate-500 mt-1">{fmtRole(evt.actorRole)}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

        </div>{/* end scrollable body */}

        {/* ── Footer ── */}
        <div className="px-6 py-4 border-t border-slate-800 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-xl text-sm text-slate-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN VIEW
// ============================================================================
const focusHighlight = (target, activeFocusTarget) =>
  (target && activeFocusTarget === target ? ' ring-2 ring-cyan-400 animate-pulse rounded-xl' : '');

// Normalize an audit log's createdAt (Timestamp | Date | number | ISO string) to ms.
function auditTimeMs(log) {
  const v = log?.createdAt;
  if (!v) return 0;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return v;
  const parsed = Date.parse(v);
  return Number.isNaN(parsed) ? 0 : parsed;
}

// Defensive newest-first ordering (the query already orders desc, but this keeps
// the display stable across mixed createdAt types and paginated "Load More" appends).
const sortByNewest = (arr) => [...arr].sort((a, b) => auditTimeMs(b) - auditTimeMs(a));

const AuditLogsView = ({ activeFocusTarget = null, autoOpenReferralId = null }) => {
  const { user } = useAuth();

  const [logs,          setLogs]          = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [loadingMore,   setLoadingMore]   = useState(false);
  const [error,         setError]         = useState(null);

  // Workflow status map: referralId → workflow/state data (batch-fetched after logs load)
  const [workflowMap,   setWorkflowMap]   = useState({});
  const resolvedWfRef = useRef(new Set());

  // User display-name map
  const [usersMap,      setUsersMap]      = useState({});
  const resolvedUidsRef = useRef(new Set());

  // Specialties for filter dropdown
  const [specialties,   setSpecialties]   = useState([]);

  // Pagination
  const [lastDoc,       setLastDoc]       = useState(null);
  const [hasMore,       setHasMore]       = useState(false);
  const [totalLoaded,   setTotalLoaded]   = useState(0);

  // Filters
  const [showFilters,   setShowFilters]   = useState(false);
  const [filters,       setFilters]       = useState({ specialtyId: '', outcomeStatus: '', startDate: '', endDate: '' });
  const [appliedFilters,setAppliedFilters] = useState({});

  // Detail modal
  const [selectedLog,   setSelectedLog]   = useState(null);

  // Deep-link: open the audit record for autoOpenReferralId when arriving from
  // "Open Referral" (Referral Pings). tenant_admin has no work-item drawer, so the
  // Audit Record detail is the safest referral surface. Fires once after logs load.
  const [deepLinkNotice, setDeepLinkNotice] = useState('');
  const autoOpenFiredRef = useRef(false);
  useEffect(() => {
    if (!autoOpenReferralId || autoOpenFiredRef.current || loading) return;
    autoOpenFiredRef.current = true;
    const match = logs.find((l) => getReferralId(l) === autoOpenReferralId);
    if (match) setSelectedLog(match);
    else setDeepLinkNotice('This referral is not available in this workspace.');
  }, [autoOpenReferralId, loading, logs]);

  // Receipt modal (full receipt with PHI, same as MySubmissionsView)
  const [receiptModal, setReceiptModal] = useState({
    open:           false,
    isLoading:      false,
    model:          null,
    workflowState:  null,
    workflowEvents: [],
    usersMap:       {},
    error:          null,
  });

  const viewLevel = getAuditViewLevel(user?.role);

  // ── Load specialties ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.tenantId) return;
    getSpecialties(user.tenantId)
      .then(s => setSpecialties(Object.values(s || {})))
      .catch(err => console.warn('[AuditLogsView] specialties:', err.message));
  }, [user?.tenantId]);

  // ── Batch-fetch workflow/state for loaded logs ──────────────────────────────
  // Gives the "Status" column the live funnel position for each referral.
  useEffect(() => {
    if (!user?.tenantId || logs.length === 0) return;
    const db = getFirestore(getApp());

    const missing = [...new Set(
      logs
        .map(l => getReferralId(l))
        .filter(id => id && id !== '—' && !workflowMap[id] && !resolvedWfRef.current.has(id))
    )];
    if (missing.length === 0) return;
    missing.forEach(id => resolvedWfRef.current.add(id));

    let cancelled = false;
    const fetchAll = async () => {
      const results = {};
      await Promise.all(missing.map(async (referralId) => {
        try {
          const snap = await getDoc(doc(db, PATHS.workflowState(user.tenantId, referralId)));
          results[referralId] = snap.exists() ? snap.data() : null;
        } catch {
          results[referralId] = null;
        }
      }));
      if (!cancelled) setWorkflowMap(prev => ({ ...prev, ...results }));
    };
    fetchAll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logs, user?.tenantId]);

  // ── Resolve user display names ──────────────────────────────────────────────
  useEffect(() => {
    if (!user?.tenantId || logs.length === 0) return;
    const missing = [...new Set(
      logs
        .filter(l => !l.createdByName && !l.createdByEmail && l.createdByUid)
        .map(l => l.createdByUid)
        .filter(uid => !usersMap[uid] && !resolvedUidsRef.current.has(uid))
    )];
    if (missing.length === 0) return;
    missing.forEach(uid => resolvedUidsRef.current.add(uid));

    let cancelled = false;
    const resolve = async () => {
      const resolved = {};
      await Promise.all(missing.map(async (uid) => {
        const profile = await getTenantUser(user.tenantId, uid);
        if (!cancelled) resolved[uid] = profile || { _failed: true };
      }));
      if (!cancelled) setUsersMap(prev => ({ ...prev, ...resolved }));
    };
    resolve();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logs, user?.tenantId]);

  // ── Load audit logs ─────────────────────────────────────────────────────────
  const loadLogs = useCallback(async (reset = true) => {
    if (!user?.tenantId) { setError('No tenant context'); setLoading(false); return; }
    if (viewLevel === AUDIT_VIEW_LEVELS.NONE) {
      setError('Access denied. Your role does not have permission to view audit logs.');
      setLoading(false);
      return;
    }

    if (reset) { setLoading(true); setLogs([]); setLastDoc(null); setTotalLoaded(0); }
    else setLoadingMore(true);
    setError(null);

    try {
      const params = { limit: PAGE_SIZE, startAfterDoc: reset ? null : lastDoc };
      if ((user.role === 'specialist' || user.role === 'specialist_admin') && user.specialtyIds?.length > 0) {
        params.specialtyIds = user.specialtyIds;
      } else if (user.role !== 'specialist' && user.role !== 'specialist_admin' && appliedFilters.specialtyId) {
        params.specialtyId = appliedFilters.specialtyId;
      }
      if (appliedFilters.outcomeStatus) params.outcomeStatus = appliedFilters.outcomeStatus;
      if (appliedFilters.startDate)     params.startDate = new Date(appliedFilters.startDate);
      if (appliedFilters.endDate)       params.endDate   = new Date(appliedFilters.endDate);

      const result = await getAuditLogsForView(user.tenantId, params);
      if (reset) {
        setLogs(sortByNewest(result.logs));
        setTotalLoaded(result.logs.length);
      } else {
        setLogs(prev => sortByNewest([...prev, ...result.logs]));
        setTotalLoaded(prev => prev + result.logs.length);
      }
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (err) {
      console.error('[AuditLogsView] load:', err);
      setError(safeUserError(err));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [user?.tenantId, user?.role, lastDoc, appliedFilters, viewLevel]);

  useEffect(() => { loadLogs(true); }, [user?.tenantId, appliedFilters]);

  const handleLoadMore     = () => { if (!loadingMore && hasMore) loadLogs(false); };
  const handleApplyFilters = () => { setAppliedFilters({ ...filters }); setShowFilters(false); };
  const handleClearFilters = () => {
    setFilters({ specialtyId: '', outcomeStatus: '', startDate: '', endDate: '' });
    setAppliedFilters({});
    setShowFilters(false);
  };

  // ── Receipt modal ──────────────────────────────────────────────────────────
  const handleViewReceipt = async (log) => {
    const referralId = getReferralId(log);
    const tenantId   = user.tenantId;

    if (!referralId || referralId === '—' || !tenantId) {
      setReceiptModal({ open: true, isLoading: false, model: null, error: 'Missing referral ID.' });
      return;
    }

    setReceiptModal({ open: true, isLoading: true, model: null, error: null });

    try {
      const specialtyId = log.specialtyId;
      const packId      = log.packId;

      const fetchWorkflowEvents = async () => {
        try {
          const db     = getFirestore(getApp());
          const colRef = collection(db, PATHS.col.workflowEvents(tenantId, referralId));
          const snap   = await getDocs(query(colRef, orderBy('createdAt', 'asc')));
          return snap.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch {
          return [];
        }
      };

      const [referralDoc, packsData, wfState, idRes, ctRes, insRes, attRes, wfEvents, metricsArr] = await Promise.all([
        getReferral(tenantId, referralId),
        getPacks(tenantId, specialtyId).catch(() => ({})),
        getWorkflowState(tenantId, referralId).catch(() => null),
        getPatientIdentity(referralId).catch(() => null),
        getContact(referralId).catch(() => null),
        getInsurance(referralId).catch(() => ({ exists: false, data: null })),
        listAttachments(referralId).catch(() => ({ attachments: [] })),
        fetchWorkflowEvents(),
        getMetricsByReferralId(tenantId, referralId).catch(() => []),
      ]);

      if (!referralDoc) {
        setReceiptModal({ open: true, isLoading: false, model: null, error: 'Referral document not found.' });
        return;
      }

      const receiptUsersMap = {};
      const actorUids = [...new Set(wfEvents.map(e => e.actorUid).filter(Boolean))];
      if (log.createdByUid && !actorUids.includes(log.createdByUid)) {
        actorUids.push(log.createdByUid);
      }
      await Promise.all(
        actorUids.map(async uid => {
          try {
            const profile = await getTenantUser(tenantId, uid);
            if (profile) receiptUsersMap[uid] = profile;
          } catch { /* fallback to role label */ }
        })
      );

      const packList = Object.values(packsData || {});
      const packDoc = packList.find(p =>
        p.id === packId || p.packId === packId || p.metadata?.id === packId
      ) || packList[0] || null;

      const packFields   = packDoc?.fields   || [];
      const packRedFlags = packDoc?.redFlags  || [];
      const packName     = packDoc?.metadata?.name    || packId     || '—';
      const packVersion  = packDoc?.metadata?.version || log.packVersion || '—';

      const missingFieldIds     = referralDoc.missingFieldIds    || [];
      const triggeredRedFlagIds = referralDoc.triggeredRedFlagIds || [];

      const missingFields = missingFieldIds
        .map(id => packFields.find(f => f.id === id))
        .filter(Boolean)
        .map(f => ({ id: f.id, label: f.label }));

      const triggeredRedFlags = triggeredRedFlagIds
        .map(id => packRedFlags.find(rf => rf.id === id))
        .filter(Boolean)
        .map(rf => ({ id: rf.id, label: rf.label }));

      const requiredFields = packFields
        .filter(f => f.required)
        .map(f => ({ id: f.id, label: f.label, required: true, helpText: f.helpText || '' }));

      const specialtyObj = specialties.find(s => s.specialtyId === specialtyId);
      const specialtyName = specialtyObj?.name || referralDoc.specialtyId || specialtyId || '—';

      const submittedByName = getDisplayName(log, { ...usersMap, ...receiptUsersMap });

      const phiIdentity  = idRes?.data  || null;
      const phiContact   = ctRes?.data  || null;
      const phiInsurance = insRes?.data  || null;
      const attachments  = attRes?.attachments || [];

      const metric = Array.isArray(metricsArr) && metricsArr.length > 0
        ? metricsArr[metricsArr.length - 1]
        : null;

      const model = buildFullReceiptModel({
        runId:               log.runId || log.id || referralId,
        referralId,
        tenantId,
        specialtyName,
        packName,
        packVersion,
        outcome:             log.outcomeStatus || referralDoc.finalOutcome || '',
        completenessScore:   metric?.completenessScore ?? referralDoc.completenessScore ?? 0,
        visitType:           referralDoc.visitType || referralDoc.telehealthContext?.visitType || '',
        reasonForEscalation: referralDoc.reasonForEscalation || referralDoc.telehealthContext?.reasonForEscalation || '',
        missingFields,
        triggeredRedFlags,
        requiredFields,
        clinicalAnswers:     referralDoc.clinicalAnswers || referralDoc.answers || {},
        overrideInfo:        (() => {
                               const docReason =
                                 referralDoc.outcome?.overrideReason ||
                                 referralDoc.override?.reason        || '';
                               if (docReason) {
                                 return {
                                   reason: docReason,
                                   note:   referralDoc.override?.note || referralDoc.outcome?.reason || '',
                                 };
                               }
                               if (wfState?.overrideReason) {
                                 return {
                                   reason: wfState.overrideReason,
                                   note:   wfState.overrideNote || '',
                                 };
                               }
                               return null;
                             })(),
        submittedAt:         referralDoc.submittedAt || referralDoc.createdAt || log.createdAt,
        submittedByName,
        submittedByRole:     'gp_employee',
        phiIdentity,
        phiContact,
        phiInsurance,
        attachments,
      });

      setReceiptModal({ open: true, isLoading: false, model, workflowState: wfState, workflowEvents: wfEvents, usersMap: receiptUsersMap, error: null });

    } catch (err) {
      console.error('[AuditLogsView] Receipt load failed:', err);
      setReceiptModal({
        open: true, isLoading: false, model: null,
        error: safeUserError(err),
      });
    }
  };

  const handleCloseReceipt = () => {
    setReceiptModal({ open: false, isLoading: false, model: null, error: null });
  };

  // ── CSV export ──────────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    if (logs.length === 0) return;
    const headers = ['Date', 'Referral ID', 'Specialty', 'Pack', 'Outcome', 'Submitted By', 'Status'];
    const rows = logs.map(log => {
      const wf     = workflowMap[getReferralId(log)];
      const wLabel = wf?.status ? getWorkflowStatusMeta(wf.status).label : '';
      return [
        formatDate(log.createdAt),
        getReferralId(log),
        log.specialtyId || '',
        log.packId || '',
        log.outcomeStatus || '',
        getDisplayName(log, usersMap),
        wLabel || (wf?.status ?? ''),
      ];
    });
    const csv = [
      headers.join(','),
      ...rows.map(r => r.map(c => `"${(c || '').toString().replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    const url  = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const activeFilterCount = Object.values(appliedFilters).filter(Boolean).length;

  // ── Error state ─────────────────────────────────────────────────────────────
  if (error && !loading) return (
    <div className="p-4 md:p-8">
      <div className="bg-slate-800/30 border border-red-500/30 rounded-2xl p-8 text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">Error Loading Audit Logs</h2>
        <p className="text-slate-400 mb-4">{error}</p>
        <button onClick={() => loadLogs(true)} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white text-sm">Retry</button>
      </div>
    </div>
  );

  // ── Loading state ───────────────────────────────────────────────────────────
  if (loading) return (
    <div className="p-4 md:p-8">
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-12 text-center">
        <Loader2 className="w-12 h-12 text-violet-400 animate-spin mx-auto mb-4" />
        <p className="text-slate-400">Loading audit logs…</p>
      </div>
    </div>
  );

  // ── Empty state ─────────────────────────────────────────────────────────────
  if (logs.length === 0) {
    const isSpecialist = user?.role === 'specialist' || user?.role === 'specialist_admin';
    return (
      <div className="p-4 md:p-8">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-white mb-2">Audit Logs</h1>
          <p className="text-slate-400">View decision history for {isSpecialist ? 'your specialty' : 'all'} referrals</p>
        </div>
        <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-12 text-center">
          <FileSearch className="w-16 h-16 text-slate-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">No Audit Logs Found</h2>
          <p className="text-slate-400">
            {Object.keys(appliedFilters).length > 0
              ? 'Try adjusting your filters to see more results.'
              : 'Audit logs will appear here when referrals are processed.'}
          </p>
          {Object.keys(appliedFilters).length > 0 && (
            <button onClick={handleClearFilters} className="mt-4 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white text-sm">
              Clear Filters
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Main view ───────────────────────────────────────────────────────────────
  return (
    <div className="p-4 md:p-8">

      {deepLinkNotice && (
        <div className="mb-4 flex items-start gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="flex-1 text-sm text-amber-300">{deepLinkNotice}</p>
          <button onClick={() => setDeepLinkNotice('')} className="text-xs text-amber-400/80 hover:text-amber-300">Dismiss</button>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div data-learning-target="ta_audit_header" className={focusHighlight('ta_audit_header', activeFocusTarget).trim()}>
            <h1 className="text-2xl font-semibold text-white mb-1">Audit Logs</h1>
            <p className="text-sm text-slate-400">
              {totalLoaded} record{totalLoaded !== 1 ? 's' : ''} loaded
              {hasMore && ' · more available'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-learning-target="ta_audit_filters"
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                showFilters || activeFilterCount > 0
                  ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30'
                  : 'bg-slate-700 hover:bg-slate-600 text-white'
              }${focusHighlight('ta_audit_filters', activeFocusTarget)}`}
            >
              <Filter className="w-4 h-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="px-1.5 py-0.5 bg-violet-500 text-white text-xs rounded-full">{activeFilterCount}</span>
              )}
            </button>
            <button
              data-learning-target="ta_audit_export"
              onClick={handleExportCSV}
              disabled={logs.length === 0}
              className={`flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 rounded-lg text-white text-sm${focusHighlight('ta_audit_export', activeFocusTarget)}`}
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button
              onClick={() => loadLogs(true)}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 rounded-lg text-white text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="mb-6 bg-slate-800/30 border border-slate-700/50 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-white">Filters</h3>
            <button onClick={() => setShowFilters(false)} className="p-1 hover:bg-slate-700 rounded-lg text-slate-400">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {user?.role === 'tenant_admin' && (
              <div>
                <label className="block text-sm text-slate-400 mb-1">Specialty</label>
                <select
                  value={filters.specialtyId}
                  onChange={(e) => setFilters(prev => ({ ...prev, specialtyId: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500"
                >
                  <option value="">All Specialties</option>
                  {specialties.map(s => <option key={s.specialtyId} value={s.specialtyId}>{s.name || s.specialtyId}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm text-slate-400 mb-1">Outcome</label>
              <select
                value={filters.outcomeStatus}
                onChange={(e) => setFilters(prev => ({ ...prev, outcomeStatus: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500"
              >
                <option value="">All Outcomes</option>
                <option value="urgent">Urgent</option>
                <option value="routine">Routine</option>
                <option value="not_yet">Not Yet</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Start Date</label>
              <input type="date" value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500 [color-scheme:dark]"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">End Date</label>
              <input type="date" value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500 [color-scheme:dark]"
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 mt-4 pt-4 border-t border-slate-700/50">
            <button onClick={handleClearFilters} className="px-4 py-2 text-slate-400 hover:text-white text-sm">Clear All</button>
            <button onClick={handleApplyFilters} className="px-4 py-2 bg-violet-500 hover:bg-violet-600 rounded-lg text-white text-sm">Apply Filters</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl overflow-hidden">

        {/* Column headers — desktop only, 12-col grid */}
        <div className="hidden md:grid md:grid-cols-12 gap-3 px-6 py-3 bg-slate-800/50 border-b border-slate-700/50 text-xs font-medium text-slate-400 uppercase tracking-wider">
          <div className="col-span-2">Date</div>
          <div className="col-span-2">Referral ID</div>
          <div className="col-span-2">Specialty / Pack</div>
          <div className="col-span-1">Outcome</div>
          <div className="col-span-2">Submitted</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1">Record</div>
        </div>

        {/* Rows */}
        <div className="divide-y divide-slate-700/50">
          {logs.map((log, logIdx) => {
            const referralId = getReferralId(log);
            const outcome    = log.outcomeStatus;
            const wf         = workflowMap[referralId];
            const wStatus    = wf?.status;

            return (
              <div
                key={log.id}
                className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 px-4 md:px-6 py-4 hover:bg-slate-800/30 transition-colors"
              >
                {/* Date */}
                <div className="col-span-1 md:col-span-2">
                  <span className="md:hidden text-xs text-slate-500 block mb-1">Date</span>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300">
                    <Calendar className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                    <span>{formatDate(log.createdAt)}</span>
                  </div>
                </div>

                {/* Referral ID */}
                <div className="col-span-1 md:col-span-2">
                  <span className="md:hidden text-xs text-slate-500 block mb-1">Referral ID</span>
                  <span className="text-sm font-mono text-white truncate block">{referralId}</span>
                </div>

                {/* Specialty / Pack */}
                <div className="col-span-1 md:col-span-2">
                  <span className="md:hidden text-xs text-slate-500 block mb-1">Specialty / Pack</span>
                  <div className="text-sm">
                    <span className="text-slate-200">{log.specialtyId || '—'}</span>
                    <span className="text-slate-500 block text-xs truncate">{log.packId || '—'}</span>
                  </div>
                </div>

                {/* Outcome */}
                <div className="col-span-1 md:col-span-1">
                  <span className="md:hidden text-xs text-slate-500 block mb-1">Outcome</span>
                  {outcome
                    ? <RGStatusBadge type="outcome" status={outcome} size="md" />
                    : <span className="text-xs text-slate-500">—</span>}
                </div>

                {/* Submitted By */}
                <div className="col-span-1 md:col-span-2">
                  <span className="md:hidden text-xs text-slate-500 block mb-1">Submitted</span>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300">
                    <User className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                    <span className="truncate">{getDisplayName(log, usersMap)}</span>
                  </div>
                </div>

                {/* Current workflow status */}
                <div className="col-span-1 md:col-span-2">
                  <span className="md:hidden text-xs text-slate-500 block mb-1">Status</span>
                  {wf === undefined ? (
                    <span className="inline-block w-20 h-5 bg-slate-700/60 rounded animate-pulse" />
                  ) : wStatus ? (
                    <RGStatusBadge type="workflow" status={wStatus} size="md" />
                  ) : (
                    <span className="text-xs text-slate-500">—</span>
                  )}
                </div>

                {/* View action */}
                <div className="col-span-1 md:col-span-1 flex items-center gap-1.5">
                  <button
                    {...(logIdx === 0 ? { 'data-learning-target': 'ta_audit_receipt' } : {})}
                    onClick={() => handleViewReceipt(log)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-700/60 hover:bg-slate-600/60 text-slate-300 hover:text-white transition-colors${logIdx === 0 ? focusHighlight('ta_audit_receipt', activeFocusTarget) : ''}`}
                  >
                    <Eye className="w-3.5 h-3.5" />
                    Receipt
                  </button>
                  {/* Audit Record (deeper Gate-run detail). Hidden for tenant_admin —
                      Receipt is their primary audit proof. Kept for other audit roles. */}
                  {user?.role !== 'tenant_admin' && (
                    <button
                      onClick={() => setSelectedLog(log)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-800/60 hover:bg-slate-700/60 text-slate-400 hover:text-white transition-colors"
                      title="Audit detail"
                    >
                      <FileSearch className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

              </div>
            );
          })}
        </div>

        {/* Load More */}
        {hasMore && (
          <div className="px-6 py-4 border-t border-slate-700/50">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="w-full py-3 flex items-center justify-center gap-2 text-slate-400 hover:text-white hover:bg-slate-800/50 rounded-xl transition-colors"
            >
              {loadingMore
                ? <><Loader2 className="w-4 h-4 animate-spin" />Loading…</>
                : <><ChevronDown className="w-4 h-4" />Load More</>
              }
            </button>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selectedLog && (
        <AuditDetailModal
          log={selectedLog}
          workflowState={workflowMap[getReferralId(selectedLog)]}
          usersMap={usersMap}
          tenantId={user?.tenantId}
          onClose={() => setSelectedLog(null)}
        />
      )}

      {/* Receipt modal */}
      {receiptModal.open && (
        <ReceiptModal
          model={receiptModal.model}
          isLoading={receiptModal.isLoading}
          error={receiptModal.error}
          workflowState={receiptModal.workflowState}
          workflowEvents={receiptModal.workflowEvents}
          usersMap={receiptModal.usersMap}
          viewLevel={viewLevel}
          onClose={handleCloseReceipt}
        />
      )}
    </div>
  );
};

export default AuditLogsView;
